// APIKeysPanel — list, create, and revoke external API keys.
//
// Keys are the only way third-party tools (Grafana, monitoring scripts, …)
// authenticate with /api/external/v1/*. The full plaintext value is shown
// exactly once, at creation time — we warn the user loudly and offer a
// one-click Copy button.
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ApiOutlined,
  CopyOutlined,
  DeleteOutlined,
  KeyOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { api, type APIKeyRow } from "../api";

const { Text, Paragraph } = Typography;

function fmtDate(unix: number | null | undefined): string {
  if (!unix) return "—";
  return new Date(unix * 1000).toLocaleString();
}

function scopeTag(s: "read" | "write" | "admin") {
  const color = s === "admin" ? "red" : s === "write" ? "orange" : "blue";
  return <Tag color={color} key={s} style={{ marginInlineEnd: 4 }}>{s}</Tag>;
}

// ---------------------------------------------------------------------------
// Modal: create new API key
// ---------------------------------------------------------------------------

interface CreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateKeyModal({ open, onClose, onCreated }: CreateModalProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [fullKey, setFullKey] = useState<string | null>(null);

  const submit = async () => {
    try {
      const vals = await form.validateFields();
      setSaving(true);
      const expires = vals.expires_at as Dayjs | undefined;
      const res = await api.createAPIKey({
        name: vals.name,
        scopes: vals.scopes,
        expires_at: expires ? Math.floor(expires.valueOf() / 1000) : null,
      });
      setFullKey(res.full);
      onCreated();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    form.resetFields();
    setFullKey(null);
    onClose();
  };

  const copyKey = async () => {
    if (!fullKey) return;
    try {
      await navigator.clipboard.writeText(fullKey);
      message.success("Key copied to clipboard");
    } catch {
      message.error("Unable to copy — select the key manually");
    }
  };

  // Post-creation screen: show the full key exactly once.
  if (fullKey) {
    return (
      <Modal
        title={<Space><KeyOutlined /> API key created</Space>}
        open={open}
        onCancel={close}
        width={560}
        footer={[
          <Button key="done" type="primary" onClick={close}>I've saved the key</Button>,
        ]}
      >
        <Alert
          type="warning"
          showIcon
          message="Copy this key now — it won't be shown again"
          description="Only the SHA-256 hash is stored. If you lose this value you'll need to create a new key."
          style={{ marginBottom: 12 }}
        />
        <div
          style={{
            fontFamily: "monospace",
            fontSize: 13,
            background: "var(--bg-2)",
            border: "1px solid var(--line)",
            borderRadius: 4,
            padding: "8px 12px",
            wordBreak: "break-all",
            marginBottom: 12,
          }}
        >
          {fullKey}
        </div>
        <Button icon={<CopyOutlined />} onClick={copyKey} block>Copy key</Button>
      </Modal>
    );
  }

  // Creation form.
  return (
    <Modal
      title={<Space><PlusOutlined /> New API Key</Space>}
      open={open}
      onCancel={close}
      onOk={submit}
      confirmLoading={saving}
      okText="Create key"
      width={520}
    >
      <Form form={form} layout="vertical" initialValues={{ scopes: ["read"] }}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Required" }]}
          extra="A label shown in the key list. Not used for auth."
        >
          <Input placeholder="e.g. grafana-prod, noc-dashboard" autoFocus />
        </Form.Item>

        <Form.Item
          name="scopes"
          label="Scopes"
          rules={[{ required: true, message: "Pick at least one scope" }]}
          extra="read: GET only · write: create/update zones & nodes · admin: flush, retention, all destructive ops"
        >
          <Checkbox.Group
            options={[
              { label: "read",  value: "read"  },
              { label: "write", value: "write" },
              { label: "admin", value: "admin" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="expires_at"
          label="Expires (optional)"
          extra="Leave empty for no expiry. Recommended for short-lived integrations."
        >
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function APIKeysPanel() {
  const [keys, setKeys] = useState<APIKeyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setKeys(await api.listAPIKeys());
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const revoke = async (id: number) => {
    try {
      await api.revokeAPIKey(id);
      message.success("Key revoked");
      reload();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const purge = async (id: number) => {
    try {
      await api.purgeAPIKey(id);
      message.success("Key permanently deleted");
      reload();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const columns: ColumnsType<APIKeyRow> = [
    {
      title: "Name",
      dataIndex: "name",
      render: (v: string, row) => (
        <Space>
          <Text strong>{v}</Text>
          {!row.enabled && <Tag color="default">revoked</Tag>}
          {row.expires_at && row.expires_at * 1000 < Date.now() && (
            <Tag color="red">expired</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Prefix",
      dataIndex: "key_prefix",
      width: 140,
      render: (v: string) => (
        <Text code style={{ fontSize: 12 }}>{v}…</Text>
      ),
    },
    {
      title: "Scopes",
      dataIndex: "scopes",
      width: 200,
      render: (v: APIKeyRow["scopes"]) => <>{v.map(scopeTag)}</>,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      width: 160,
      render: (v: number) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(v)}</Text>
      ),
    },
    {
      title: "Last used",
      dataIndex: "last_used_at",
      width: 220,
      render: (_: unknown, row) => (
        <Tooltip title={row.last_used_ip ? `from ${row.last_used_ip}` : undefined}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {fmtDate(row.last_used_at)}
            {row.last_used_ip ? ` · ${row.last_used_ip}` : ""}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Expires",
      dataIndex: "expires_at",
      width: 140,
      render: (v: number | null) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {v ? fmtDate(v) : "never"}
        </Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 110,
      render: (_: unknown, row) =>
        row.enabled ? (
          <Popconfirm
            title="Revoke this key?"
            description="Third-party tools using this key will start getting 401s immediately."
            okText="Revoke"
            okButtonProps={{ danger: true }}
            onConfirm={() => revoke(row.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>Revoke</Button>
          </Popconfirm>
        ) : (
          // Already revoked — offer permanent delete to clean up the list.
          // Two-click flow (revoke → delete) keeps an audit trail by default
          // while still letting admins clear old test keys when they want.
          <Popconfirm
            title="Permanently delete this key?"
            description="The audit row will be erased. This cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => purge(row.id)}
          >
            <Button size="small" danger type="text" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <>
      <Card
        size="small"
        title={
          <Space>
            <ApiOutlined />
            <span style={{ fontWeight: 700 }}>External API Keys</span>
          </Space>
        }
        extra={
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
          >
            New key
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          These keys authenticate third-party tools against{" "}
          <Text code>/api/external/v1/*</Text>. Send them as{" "}
          <Text code>Authorization: Bearer sfk_…</Text> or{" "}
          <Text code>X-API-Key: sfk_…</Text>.
        </Paragraph>
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={keys}
          pagination={false}
          locale={{ emptyText: "No API keys yet — click New key to create one." }}
        />
      </Card>

      <CreateKeyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={reload}
      />
    </>
  );
}
